// =============================================================================
// Filtro de período — os dois campos do protótipo, "Mês" e "Ano"
//
// O mês é uma lista suspensa com caixas de seleção e um botão **Aplicar**.
//
// ⚠️ O Aplicar não é enfeite, é o conserto do defeito que o Financeiro apontou:
// marcar mês por mês recarregava a tela a cada clique. Aqui as marcações ficam
// num rascunho enquanto a lista está aberta, e **uma única atualização**
// acontece quando se aplica. Fechar sem aplicar descarta — o que estava valendo
// continua valendo.
//
// ⚠️ Competência é texto no dia 1, do começo ao fim. Nunca `Date`: converter no
// caminho faria o mês andar conforme o fuso de quem abre a tela, que é a
// armadilha que os testes de `lib/formato.ts` guardam.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import { competenciaCurta } from "../lib/formato.ts";

const MESES = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

const comp = (ano: number, mes: number) => `${ano}-${String(mes).padStart(2, "0")}-01`;

/** O resumo do período — no botão fechado e no texto acima dos indicadores. */
export function resumoPeriodo(periodo: string[]): string {
  if (periodo.length === 0) return "Nenhum mês";
  if (periodo.length === 1) return competenciaCurta(periodo[0]!);

  const ordenado = [...periodo].sort();
  const anos = new Set(ordenado.map((c) => c.slice(0, 4)));
  const contiguo =
    anos.size === 1 &&
    ordenado.every((c, i) => {
      if (i === 0) return true;
      return Number(c.slice(5, 7)) === Number(ordenado[i - 1]!.slice(5, 7)) + 1;
    });

  if (contiguo) {
    const primeiro = MESES[Number(ordenado[0]!.slice(5, 7)) - 1];
    return `${primeiro}–${competenciaCurta(ordenado[ordenado.length - 1]!)}`;
  }
  return `${periodo.length} meses`;
}

const CLASSE_CAMPO =
  "flex items-center justify-between gap-2 rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px] text-tinta";
// `compacto`: o tamanho da barra de filtros das grades (Grade.tsx), pedida
// menor pelo Financeiro em 21/10. As outras telas seguem no tamanho de sempre.
const CLASSE_CAMPO_COMPACTO =
  "flex items-center justify-between gap-2 rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px] text-tinta";

export function FiltroMes({
  periodo,
  onMudar,
  compacto = false,
}: {
  periodo: string[];
  onMudar: (p: string[]) => void;
  compacto?: boolean;
}) {
  const ano = Number(periodo[periodo.length - 1]!.slice(0, 4));
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<Set<string>>(new Set());
  const caixa = useRef<HTMLDivElement>(null);

  function abrir() {
    // O rascunho nasce do que está valendo: abrir a lista não muda nada.
    setRascunho(new Set(periodo.map((c) => c.slice(5, 7))));
    setAberto(true);
  }

  // Fecha no Esc e no clique fora, DESCARTANDO o rascunho — fechar sem aplicar
  // não pode alterar a tela, senão o botão Aplicar não significaria nada.
  useEffect(() => {
    if (!aberto) return;
    const aoClicar = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", aoClicar);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicar);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  function alternar(mes: string) {
    setRascunho((atual) => {
      const novo = new Set(atual);
      if (novo.has(mes)) novo.delete(mes);
      else novo.add(mes);
      return novo;
    });
  }

  function aplicar() {
    onMudar([...rascunho].sort().map((m) => `${ano}-${m}-01`));
    setAberto(false);
  }

  return (
    <div ref={caixa} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={aberto}
        onClick={() => (aberto ? setAberto(false) : abrir())}
        className={compacto ? `${CLASSE_CAMPO_COMPACTO} min-w-[120px]` : `${CLASSE_CAMPO} min-w-[150px]`}
      >
        <span className="num">{resumoPeriodo(periodo)}</span>
        <span aria-hidden className="text-[10px] text-tinta-fraca">▼</span>
      </button>

      {aberto && (
        <div
          role="group"
          aria-label="Meses"
          className="surface-card absolute top-full left-0 z-30 mt-1 w-[215px] p-1.5"
        >
          <ul className="thin-scroll max-h-[240px] overflow-y-auto">
            {MESES.map((rotulo, i) => {
              const mes = String(i + 1).padStart(2, "0");
              const marcado = rascunho.has(mes);
              return (
                <li key={rotulo}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] hover:bg-superficie-2 ${
                      marcado ? "font-semibold" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => alternar(mes)}
                      className="size-3.5 accent-[color:var(--color-laranja)]"
                    />
                    <span className="num">
                      {rotulo}/{String(ano).slice(2)}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>

          <div className="mt-1.5 border-t border-linha px-1 pt-1.5">
            <div className="flex items-center justify-between gap-2">
              {/* Todos e Limpar mexem no RASCUNHO, não na tela: marcar os doze
                  e aplicar continua sendo uma atualização só. */}
              <div className="flex gap-2.5">
                <button
                  type="button"
                  onClick={() => setRascunho(new Set(MESES.map((_, i) => String(i + 1).padStart(2, "0"))))}
                  className="text-[11.5px] font-semibold text-azul-medio hover:underline"
                >
                  Todos
                </button>
                <button
                  type="button"
                  onClick={() => setRascunho(new Set())}
                  className="text-[11.5px] font-semibold text-azul-medio hover:underline"
                >
                  Limpar
                </button>
              </div>
              <span className="num text-[11px] text-tinta-fraca">
                {rascunho.size} {rascunho.size === 1 ? "mês" : "meses"}
              </span>
            </div>
            <button
              type="button"
              // Sem mês nenhum não há o que somar, e a requisição seguinte
              // seria recusada pela API — melhor não deixar chegar lá. É também
              // o que dá sentido ao "Limpar": ele esvazia para escolher de
              // novo, não para aplicar vazio.
              disabled={rascunho.size === 0}
              onClick={aplicar}
              className="mt-1.5 w-full rounded-lg bg-laranja px-3 py-1.5 text-[12px] font-bold text-azul disabled:cursor-not-allowed disabled:opacity-45"
            >
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** O campo "Ano". Trocar o ano preserva os meses marcados. */
export function FiltroAno({
  periodo,
  onMudar,
  exercicios,
  compacto = false,
}: {
  periodo: string[];
  onMudar: (p: string[]) => void;
  exercicios?: number[];
  compacto?: boolean;
}) {
  const ano = Number(periodo[periodo.length - 1]!.slice(0, 4));
  const anos = exercicios?.length ? [...new Set([...exercicios, ano])].sort() : [ano];

  return (
    <select
      aria-label="Ano"
      value={ano}
      onChange={(e) => {
        const novo = Number(e.target.value);
        onMudar(periodo.map((c) => comp(novo, Number(c.slice(5, 7)))).sort());
      }}
      className={compacto
        ? "min-w-[84px] rounded-lg border border-linha bg-superficie px-2.5 py-1.5 text-[12.5px] text-tinta"
        : "min-w-[100px] rounded-lg border border-linha bg-superficie px-3 py-2 text-[13px] text-tinta"}
    >
      {anos.map((a) => (
        <option key={a} value={a}>
          {a}
        </option>
      ))}
    </select>
  );
}
